import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

// Enhanced Firebase configuration with fallbacks and validation
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'AIzaSyApRiY44YXTtzrf5yE511aJbyvxoplzt_0',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'hyrox-66241.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'hyrox-66241',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'hyrox-66241.firebasestorage.app',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '713756899032',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:713756899032:web:a5975ff47ef6079f6749ad',
};

// Enhanced Firebase configuration validation
function validateFirebaseConfig() {
  const missingVars = [];
  const warnings = [];

  // Check required fields
  if (!firebaseConfig.apiKey || firebaseConfig.apiKey === 'your_api_key_here') {
    missingVars.push('VITE_FIREBASE_API_KEY');
  }
  if (!firebaseConfig.authDomain) missingVars.push('VITE_FIREBASE_AUTH_DOMAIN');
  if (!firebaseConfig.projectId) missingVars.push('VITE_FIREBASE_PROJECT_ID');
  if (!firebaseConfig.storageBucket) missingVars.push('VITE_FIREBASE_STORAGE_BUCKET');
  if (!firebaseConfig.messagingSenderId) missingVars.push('VITE_FIREBASE_MESSAGING_SENDER_ID');
  if (!firebaseConfig.appId) missingVars.push('VITE_FIREBASE_APP_ID');

  // Validate API key format
  if (firebaseConfig.apiKey && !firebaseConfig.apiKey.startsWith('AIza')) {
    warnings.push('API key should start with "AIza"');
  }

  // Validate auth domain format
  if (firebaseConfig.authDomain && !firebaseConfig.authDomain.includes('.firebaseapp.com')) {
    warnings.push('Auth domain should end with ".firebaseapp.com"');
  }

  // Check if auth domain matches project ID (recommended)
  if (firebaseConfig.authDomain && firebaseConfig.projectId) {
    const expectedDomain = `${firebaseConfig.projectId}.firebaseapp.com`;
    if (firebaseConfig.authDomain !== expectedDomain) {
      warnings.push(`Auth domain should typically be "${expectedDomain}" for project "${firebaseConfig.projectId}"`);
    }
  }

  return { missingVars, warnings };
}

// Validate configuration
const validation = validateFirebaseConfig();

if (validation.missingVars.length > 0) {
  console.error('Missing Firebase environment variables:', validation.missingVars);
  console.error('Using fallback configuration values');
}

if (validation.warnings.length > 0) {
  console.warn('Firebase configuration warnings:', validation.warnings);
}

if (import.meta.env.MODE === 'production' && validation.missingVars.length === 0) {
  console.log('Firebase configuration validated for production');
}

// Initialize Firebase with error handling
let app;
let auth;
let db;
let storage;

try {
  app = initializeApp(firebaseConfig);
  console.log('Firebase app initialized successfully');

  // Initialize Firebase services with error handling
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);

  console.log('Firebase services initialized successfully');
} catch (error) {
  console.error('Failed to initialize Firebase:', error);
  throw new Error(`Firebase initialization failed: ${error.message}`);
}

// Connect to emulators only in development and when explicitly enabled
const useEmulator = import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true';

if (useEmulator) {
  try {
    // Only connect to emulators if they're not already connected
    if (!auth._delegate?._config?.emulator) {
      connectAuthEmulator(auth, 'http://localhost:9100');
      console.log('Connected to Firebase Auth emulator');
    }

    if (!db._delegate?._databaseId?.projectId?.includes('demo-')) {
      connectFirestoreEmulator(db, 'localhost', 8081);
      console.log('Connected to Firestore emulator');
    }
  } catch (error) {
    console.warn('Failed to connect to Firebase emulators:', error);
    // App will continue to work with production Firebase
  }
}

// Export services
export { auth, db, storage };

export default app;
